import {
  format,
  formatDistanceToNowStrict,
  subHours,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import type { IActionItemGlobalItemOrId } from '@model/actionItem';

interface IResult {
  needResponseList: IActionItemGlobalItemOrId[];
  waitingList: IActionItemGlobalItemOrId[];
  neighborAisById: {
    [key: string]: { prevId: number | null; nextId: number | null; currentPosition: number };
  };
}

const getUpdatedAiFields = (
  t: (text: string) => string,
  dateTime: string,
  userTimezone: string,
  actionItem: IActionItemGlobalItemOrId,
) => {
  const updatedAtWithTimezone = new Date(actionItem.updated_at.replace(' ', 'T') + 'Z');

  const currentDateInUtc = format(toZonedTime(new Date(), 'UTC'), 'yyyy-MM-dd HH:mm:ss');

  const hoursQuantity = +actionItem.life_time_value % 24;

  const lifetimeInDays = formatDistanceToNowStrict(subHours(currentDateInUtc, actionItem.life_time_value), {
    unit: 'day',
    roundingMethod: hoursQuantity > 12 ? 'round' : 'floor',
  });

  const lifeTime =
    +actionItem.life_time_value > 24 ? lifetimeInDays : `${+actionItem.life_time_value} ${t('hours')}`;

  return {
    lifeTime,
    time_created: actionItem.time_created
      ? format(fromZonedTime(actionItem.time_created, userTimezone), dateTime)
      : '-',
    updated_at: actionItem.updated_at ? format(updatedAtWithTimezone, dateTime) : '-',
    update_distance: actionItem.updated_at
      ? `${t('Updated at')} ${formatDistanceToNowStrict(updatedAtWithTimezone, {
          addSuffix: true,
          roundingMethod: 'floor',
        })}`
      : '',
  };
};

export const getNeighborAisById = (
  t: (text: string) => string,
  dateFormat: string,
  aiNeedResponseList: IActionItemGlobalItemOrId[],
  aiWaitingList: IActionItemGlobalItemOrId[],
) => {
  const result: IResult = { needResponseList: [], waitingList: [], neighborAisById: {} };
  const allAisList = [...aiNeedResponseList, ...aiWaitingList];
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  allAisList.forEach((ai, i) => {
    const updatedFields = getUpdatedAiFields(t, dateFormat, userTimezone, ai);
    const updatedAi = { ...ai, ...updatedFields };

    if ((+ai.state === 0 || +ai.state === 10) && !ai.is_troubleshooting_hold) {
      result.needResponseList.push(updatedAi);
    } else {
      result.waitingList.push(updatedAi);
    }

    result.neighborAisById[ai.id] = {
      prevId: allAisList[i - 1]?.id,
      nextId: allAisList[i + 1]?.id,
      currentPosition: i + 1,
    };
  });

  return result;
};
